/**
 * dashboard-layout.ts
 * Shared Zod contract for the editable dashboards (home and per module)
 * (odd/tasks/editable-dashboard.md, S2–S3).
 *
 * A layout is an ordered list of widgets, each with a size on a 12-column grid:
 * S = 3 columns, M = 6, L = 12. The widget keys are an open vocabulary here (the
 * frontend registry owns the catalog), so a retired widget never breaks a stored
 * layout: readers ignore keys they do not know.
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 */
import { z } from 'zod';

/**
 * Which dashboard a layout belongs to: the home one or a module's
 * (odd/tasks/module-dashboards.md, S2). Sent as `?dashboard=`; absent means
 * 'home', so older clients keep working.
 */
export const DASHBOARD_SCOPES = ['home', 'rrhh', 'servicios', 'compras', 'proveedores'] as const;
export const DashboardScopeSchema = z.enum(DASHBOARD_SCOPES);
export type DashboardScope = z.infer<typeof DashboardScopeSchema>;

export const DashboardWidgetSizeSchema = z.enum(['S', 'M', 'L']);
export type DashboardWidgetSize = z.infer<typeof DashboardWidgetSizeSchema>;

export const DashboardLayoutItemSchema = z.object({
  widget: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/).max(64),
  size: DashboardWidgetSizeSchema,
});
export type DashboardLayoutItem = z.infer<typeof DashboardLayoutItemSchema>;

/** PUT body for the user's layout and for the company default. */
export const DashboardLayoutBodySchema = z
  .object({ items: z.array(DashboardLayoutItemSchema).max(30) })
  .refine((l) => new Set(l.items.map((i) => i.widget)).size === l.items.length, {
    message: 'Each widget can appear only once.',
    path: ['items'],
  });
export type DashboardLayoutBody = z.infer<typeof DashboardLayoutBodySchema>;

/**
 * GET /me/dashboard-layout (also returned by PUT/DELETE on it): the layout the
 * user sees and where it comes from. `items: null` with source 'builtin' means
 * "no stored layout anywhere" — the frontend applies its built-in default.
 */
export const DashboardLayoutResponseSchema = z.object({
  items: z.array(DashboardLayoutItemSchema).nullable(),
  source: z.enum(['user', 'company', 'builtin']),
  /** Whether this user may save the company default (tenant.empresa.editar). */
  canEditCompanyDefault: z.boolean(),
});
export type DashboardLayoutResponse = z.infer<typeof DashboardLayoutResponseSchema>;
